import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import {
  ArchiveBoxXMarkIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  StopCircleIcon,
} from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import ScanQrCode from "../shared/ScanQrCode";

interface ScanVehicleActionModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  /** Name of the assembly whose timer is currently running. */
  assemblyName?: string;
  /** How many parts in that assembly are still outstanding. */
  outstandingCount: number;
  /** The job's vehicle, used to render a QR the user can re-scan to finish. */
  vehicleId?: string;
  vehicleVrm?: string | null;
  onMarkAllRemoved: () => void;
  onMarkAllDamaged: () => void;
  onPause: () => void;
  onFinish: () => void;
}

// Shown when the job's vehicle is scanned while an assembly timer is running and
// parts are still outstanding. The user can clear the remaining parts in bulk,
// pause or finish the timer. Scanning the vehicle again while this is open
// marks remaining parts as removed and finishes the timer (handled by the caller).
export default function ScanVehicleActionModal({
  open,
  setOpen,
  assemblyName,
  outstandingCount,
  vehicleId,
  vehicleVrm,
  onMarkAllRemoved,
  onMarkAllDamaged,
  onPause,
  onFinish,
}: ScanVehicleActionModalProps) {
  const { t } = useTranslation("jobs");

  const actionButton =
    "inline-flex flex-1 items-center justify-center gap-x-2 rounded-md border border-transparent shadow-sm px-4 py-3 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500";

  return (
    <Modal open={open} setOpen={setOpen} width={"max-w-lg"}>
      <div className="text-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {t("{{name}} timer running", { name: assemblyName ?? t("Assembly") })}
        </h3>
        <p className="text-sm text-gray-600 mt-2">
          {outstandingCount === 1
            ? t("{{count}} part still needs removing or marking as damaged.", { count: outstandingCount })
            : t("{{count}} parts still need removing or marking as damaged.", { count: outstandingCount })}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {t("Scan the vehicle again to remove remaining parts and finish the timer.")}
        </p>

        {vehicleId && (
          <div className="mt-4 flex flex-col items-center gap-1">
            <ScanQrCode
              value={`v|${vehicleId}|${vehicleVrm ?? "NULL"}`}
              alt={t("Vehicle QR code")}
              className="h-40 w-40"
            />
            <p className="text-xs text-gray-400">{t("Scan to remove remaining and finish")}</p>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button type="button" className={`${actionButton} bg-primary-600 hover:bg-primary-700`} onClick={onMarkAllRemoved}>
          <ArchiveBoxXMarkIcon className="h-5 w-5" aria-hidden="true" />
          {t("Remove Remaining")}
        </button>
        <button type="button" className={`${actionButton} bg-red-600 hover:bg-red-700`} onClick={onMarkAllDamaged}>
          <ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" />
          {t("Mark Remaining as Damaged")}
        </button>
        <button type="button" className={`${actionButton} bg-amber-500 hover:bg-amber-600`} onClick={onPause}>
          <PauseCircleIcon className="h-5 w-5" aria-hidden="true" />
          {t("Pause Timer")}
        </button>
        <button type="button" className={`${actionButton} bg-green-600 hover:bg-green-700`} onClick={onFinish}>
          <StopCircleIcon className="h-5 w-5" aria-hidden="true" />
          {t("Finish Timer")}
        </button>
      </div>

      <button
        type="button"
        className="mt-4 w-full inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 bg-white text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500 sm:text-sm"
        onClick={() => setOpen(false)}
      >
        {t("Cancel")}
      </button>
    </Modal>
  );
}
